/**
 * SEO helpers for public `/canvas/:slug` routes.
 * Project metadata comes from `publicOpenSourceProjects.json`.
 */
import { getPublicProjects } from "../common/publicUtils";
import {
  getActivePublicOpenSourceSlugs,
  getPublicOpenSourceDisplayName,
  getPublicOpenSourceProject,
} from "./publicOpenSourceProjects";

type PublicCanvasSeo = {
  title: string;
  description: string;
};

export type PublicCanvasSeoNavItem = {
  slug: string;
  name: string;
  path: string;
  label: string;
};

function buildCanvasSeoLinkLabel(name: string): string {
  return `${name} architecture visualization generated by JigsawML`;
}

function buildCanvasDescription(name: string): string {
  return `Explore an interactive ${name} architecture diagram generated by JigsawML. See components, dependencies, and how ${name} is structured — no login required.`;
}

function isActivePublicSlug(slug: string): boolean {
  return Boolean(getPublicProjects()[slug] && getPublicOpenSourceProject(slug));
}

/** Crawlable `/canvas/:slug` links for homepage SEO nav. */
export function getPublicCanvasSeoNavItems(): PublicCanvasSeoNavItem[] {
  return getActivePublicOpenSourceSlugs().map((slug) => {
    const name = getPublicOpenSourceDisplayName(slug);
    return {
      slug,
      name,
      path: `/canvas/${slug}`,
      label: buildCanvasSeoLinkLabel(name),
    };
  });
}

export function getPublicCanvasSeo(slug: string): PublicCanvasSeo | null {
  if (!isActivePublicSlug(slug)) {
    return null;
  }

  const name = getPublicOpenSourceDisplayName(slug);
  return {
    title: `${name} - JigsawML`,
    description: buildCanvasDescription(name),
  };
}

/** Page heading for public `/canvas/:slug` routes (crawler-friendly). */
export function getPublicCanvasHeading(slug: string): string | null {
  if (!isActivePublicSlug(slug)) {
    return null;
  }

  const name = getPublicOpenSourceDisplayName(slug);
  return `${name} architecture — generated by JigsawML`;
}
